import { create } from "zustand"
import { persist } from "zustand/middleware"

interface HistoryEntry {
  tags: string[]
  timestamp: number
}

interface TagHistoryState {
  history: HistoryEntry[]
  currentIndex: number
  addToHistory: (tags: string[]) => void
  goBack: () => string[] | null
  goForward: () => string[] | null
  clearHistory: () => void
  canGoBack: () => boolean
  canGoForward: () => boolean
}

export const useTagHistoryStore = create<TagHistoryState>()(
  persist(
    (set, get) => ({
      history: [],
      currentIndex: -1,

      addToHistory: (tags: string[]) => {
        const state = get()
        const newEntry: HistoryEntry = { tags, timestamp: Date.now() }

        // Remove any forward history if we're not at the end
        const newHistory = state.history.slice(0, state.currentIndex + 1)

        // Don't add duplicate entries
        if (newHistory.length > 0 && newHistory[newHistory.length - 1].tags.join(",") === tags.join(",")) {
          return
        }

        newHistory.push(newEntry)

        // Limit history to last 50 entries
        if (newHistory.length > 50) {
          newHistory.shift()
        }

        set({
          history: newHistory,
          currentIndex: newHistory.length - 1,
        })
      },

      goBack: () => {
        const state = get()
        if (state.currentIndex > 0) {
          const newIndex = state.currentIndex - 1
          set({ currentIndex: newIndex })
          return state.history[newIndex].tags
        }
        return null
      },

      goForward: () => {
        const state = get()
        if (state.currentIndex < state.history.length - 1) {
          const newIndex = state.currentIndex + 1
          set({ currentIndex: newIndex })
          return state.history[newIndex].tags
        }
        return null
      },

      clearHistory: () => set({ history: [], currentIndex: -1 }),

      canGoBack: () => {
        const state = get()
        return state.currentIndex > 0
      },

      canGoForward: () => {
        const state = get()
        return state.currentIndex < state.history.length - 1
      },
    }),
    {
      name: "tag-history",
    },
  ),
)
